import { apiGet, apiPost, apiPut, apiDelete, apiPatch } from './apiUtils';

export interface Organization {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

/** API: is_active (snake_case). По умолчанию считаем активной, если поле отсутствует. */
export interface Specialty {
  id: number;
  name: string;
  description?: string;
  structure?: string;
  organization_id?: number;
  organization?: Organization;
  profession?: string;
  is_active?: boolean;
  created_at: string;
  updated_at: string;
}

export interface SpecialtyWithSelection extends Specialty {
  is_selected: boolean;
}

export interface AdminUser {
  id: number;
  phone_number: string;
  first_name: string;
  last_name: string;
  email: string;
  specialty?: string; // Устаревшее поле, для обратной совместимости
  specialties?: Specialty[]; // Новое поле - массив специальностей
  organization: string;
  mis_city?: string; // Город МИС (для организации Полимедика)
  has_active_subscription?: boolean;
  active_subscription_status?: SubscriptionStatus;
  active_subscription_next_billing_date?: string | null;
  role?: {
    id: number;
    name: string;
  };
  role_id?: number; // Альтернативное поле для роли, если API возвращает role_id вместо объекта role
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserListResponse {
  users: AdminUser[];
  total: number;
  limit: number;
  offset: number;
  has_next: boolean;
}

export interface CreateUserData {
  phone_number?: string;
  first_name: string;
  last_name: string;
  email: string;
  specialty?: string; // Опционально, для обратной совместимости
  organization?: string;
  password: string;
  confirm_password: string;
  role_id: number;
}

export interface UpdateUserData {
  phone_number?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  specialty?: string; // Устаревшее строковое поле specialty, для обратной совместимости
  organization?: string;
  mis_city?: string; // Город МИС (для организации Полимедика)
  role_id?: number;
}

export interface UpdatePasswordData {
  password: string;
  confirm_password: string;
}

// Получить список пользователей
export const getUsersList = async (params: {
  limit?: number;
  offset?: number;
  search?: string;
  role_id?: number;
  organization?: string;
  specialty?: string;
  is_active?: boolean;
}): Promise<UserListResponse> => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      searchParams.append(key, value.toString());
    }
  });

  const response = await apiGet(`/auth/admin/users?${searchParams}`);

  if (!response.ok) {
    throw new Error('Ошибка загрузки пользователей');
  }

  return response.json();
};

// Получить детали пользователя
export const getUserDetails = async (userId: number): Promise<AdminUser> => {
  const response = await apiGet(`/auth/admin/users/${userId}`);

  if (!response.ok) {
    throw new Error('Ошибка загрузки данных пользователя');
  }

  return response.json();
};

// Обновить пользователя
export const updateUser = async (userId: number, data: UpdateUserData): Promise<AdminUser> => {
  const response = await apiPut(`/auth/admin/users/${userId}`, data);

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Ошибка обновления пользователя');
  }

  return response.json();
};

// Обновить пароль пользователя
export const updateUserPassword = async (userId: number, data: UpdatePasswordData): Promise<{ message: string; user_id: number }> => {
  const response = await apiPut(`/auth/admin/users/${userId}/password`, data);

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Ошибка обновления пароля');
  }

  return response.json();
};

// Обновить статус активации пользователя
export const updateUserActivationStatus = async (userId: number, isActive: boolean): Promise<AdminUser> => {
  const response = await apiPut(`/auth/admin/users/${userId}`, { is_active: isActive });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Ошибка обновления статуса активации');
  }

  return response.json();
};

// Удалить пользователя
export const deleteUser = async (userId: number): Promise<{ message: string; user_id: number }> => {
  const response = await apiDelete(`/auth/admin/users/${userId}`);

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Ошибка удаления пользователя');
  }

  return response.json();
};

// Создать администратора
export const createAdmin = async (data: CreateUserData): Promise<{ message: string; user_id: number; email: string; role: string }> => {
  const response = await apiPost('/auth/create_admin', data);

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Ошибка создания пользователя');
  }

  return response.json();
};

// Роли системы
export const ROLES = {
  1: { name: 'User', displayName: 'Пользователь' },
  2: { name: 'Moderator', displayName: 'Модератор' },
  3: { name: 'Admin', displayName: 'Администратор' },
  4: { name: 'SuperAdmin', displayName: 'Супер-администратор' }
} as const;

export const getRoleDisplayName = (roleId: number): string => {
  return ROLES[roleId as keyof typeof ROLES]?.displayName || 'Неизвестная роль';
};

export const getRoleBadgeClass = (roleId: number): string => {
  const roleNames = {
    1: 'user',
    2: 'moderator',
    3: 'admin',
    4: 'superadmin'
  } as const;

  return roleNames[roleId as keyof typeof roleNames] || 'user';
};

// -----------------------------
// Тарифы и подписки
// -----------------------------

export type ProtocolLimitPeriod = 'monthly' | 'yearly';

export interface AdminTariff {
  id: number;
  name: string;
  description?: string | null;
  price_monthly: number;
  price_yearly_total: number;
  price_yearly_monthly: number;
  currency: string;
  protocol_limit: number;
  protocol_limit_period: ProtocolLimitPeriod;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TariffPayload {
  name: string;
  description?: string;
  price_monthly: number;
  price_yearly_total: number;
  price_yearly_monthly: number;
  currency?: string;
  protocol_limit: number;
  protocol_limit_period: ProtocolLimitPeriod;
  is_active?: boolean;
}

export const getTariffs = async (): Promise<AdminTariff[]> => {
  const response = await apiGet('/subscriptions/tariffs');

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка загрузки тарифов');
  }

  return response.json();
};

export const createTariff = async (data: TariffPayload): Promise<AdminTariff> => {
  const response = await apiPost('/subscriptions/tariffs', data);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка создания тарифа');
  }

  return response.json();
};

export const updateTariff = async (
  tariffId: number,
  data: Partial<TariffPayload>
): Promise<AdminTariff> => {
  const response = await apiPut(`/subscriptions/tariffs/${tariffId}`, data);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка обновления тарифа');
  }

  return response.json();
};

export type SubscriptionStatus = 'active' | 'expired' | 'canceled';
export type BillingPeriod = 'monthly' | 'yearly';
export type TariffPeriod = 'monthly' | 'yearly';

export interface AdminSubscriptionSummary {
  id: number;
  user_id: number;
  user_email: string;
  user_full_name: string;
  organization: string;
  tariff_name: string;
  status: SubscriptionStatus;
  billing_period: BillingPeriod;
  protocol_limit_total: number;
  protocols_balance: number;
  next_billing_date: string | null;
  activated_at: string | null;
  last_payment_confirmed_at: string | null;
  discount_multiplier: number;
}

export interface AdminSubscriptionsResponse {
  total: number;
  items: AdminSubscriptionSummary[];
}

// Получение активной подписки пользователя администратором
// Используем GET /subscriptions/active-subscription
// Для администратора может потребоваться передать user_id как параметр
export const getUserSubscription = async (
  userId: number
): Promise<SubscriptionDetailsResponse | null> => {
  try {
    // Пробуем с параметром user_id для администратора
    const response = await apiGet(`/subscriptions/active-subscription?user_id=${userId}`);
    if (response.ok) {
      const data = await response.json();
      // Проверяем структуру ответа - может быть { subscription: {...}, payments: [...] } или просто {...}
      if (data.subscription) {
        return data as SubscriptionDetailsResponse;
      }
      // Если ответ - это объект подписки напрямую, оборачиваем его
      if (data.id && data.user_id) {
        return {
          subscription: data as SubscriptionDetails,
          payments: []
        };
      }
      return data;
    }
    if (response.status === 404) {
      return null; // У пользователя нет активной подписки
    }
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.message || `Ошибка получения подписки: ${response.status}`);
  } catch (err) {
    // Если 404 - у пользователя нет подписки
    if (err instanceof Error && (err.message.includes('404') || err.message.includes('Not Found'))) {
      return null;
    }
    throw err;
  }
};

export interface SubscriptionDetails {
  id: number;
  user_id: number;
  tariff_id: number;
  tariff?: AdminTariff;
  status: SubscriptionStatus;
  billing_period: BillingPeriod;
  protocol_limit_period: ProtocolLimitPeriod;
  protocol_limit_total: number;
  protocols_balance: number;
  locked_price_monthly: number;
  locked_price_yearly_total: number;
  locked_price_yearly_monthly: number;
  currency: string;
  current_period_start: string | null;
  current_period_end: string | null;
  next_billing_date: string | null;
  activated_at: string | null;
  canceled_at: string | null;
  last_payment_confirmed_at: string | null;
  auto_renew: boolean;
  rebill_id: string | null;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionPayment {
  id: number;
  subscription_id: number;
  amount: number;
  currency: string;
  billing_period: BillingPeriod;
  period_start: string;
  period_end: string;
  paid_at: string;
  payment_id: string | null;
  rebill_id: string | null;
  external_reference?: string | null;
  notes?: string | null;
}

export interface SubscriptionDetailsResponse {
  subscription: SubscriptionDetails;
  payments: SubscriptionPayment[];
}

// УДАЛЕНО: Эндпоинт для получения деталей подписки не существует в новой документации API
// Используйте управление подписками через раздел "Пользователи"

export interface CreateSubscriptionPayload {
  user_id: number;
  tariff_id: number;
  billing_period: BillingPeriod;
  protocol_limit_total?: number;
  protocols_balance?: number;
  auto_renew?: boolean;
  notes?: string;
}

export const createSubscriptionForUser = async (
  data: CreateSubscriptionPayload
): Promise<SubscriptionDetailsResponse> => {
  const response = await apiPost('/subscriptions/subscribe', data);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка создания подписки');
  }

  return response.json();
};

export interface EditSubscriptionPayload {
  user_id: number;
  tariff_id?: number;
  billing_period?: BillingPeriod;
  status?: SubscriptionStatus;
  protocol_limit_total?: number;
  protocols_balance?: number;
  notes?: string;
  update_protocol_limit?: boolean;
  reset_balance_to_total?: boolean;
}

export const editSubscription = async (
  data: EditSubscriptionPayload
): Promise<SubscriptionDetailsResponse> => {
  const response = await apiPut('/subscriptions/edit-subscription', data);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка редактирования подписки');
  }

  return response.json();
};

export const deleteSubscription = async (
  userId: number
): Promise<void> => {
  const response = await apiDelete('/subscriptions/delete-subscription', {
    user_id: userId
  });

  // 204 No Content - успешное удаление, не нужно парсить ответ
  if (response.status === 204 || response.ok) {
    return;
  }

  // Обработка ошибок
  const errorData = await response.json().catch(() => ({}));
  throw new Error(errorData.detail || errorData.message || `Ошибка удаления подписки (${response.status})`);
};

// Удалены updateSubscriptionLimits и resetSubscriptionLimit - теперь используется editSubscription

export interface ConfirmPaymentPayload {
  tariff_id: number;
  billing_period: BillingPeriod;
  amount?: number;
  payment_id?: string;
  rebill_id?: string;
  paid_at?: string;
  external_reference?: string;
  notes?: string;
}

export const confirmSubscriptionPayment = async (
  data: ConfirmPaymentPayload
): Promise<{ subscription: SubscriptionDetails; payments: SubscriptionPayment[] }> => {
  const response = await apiPost('/subscriptions/confirm-payment', data);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка подтверждения оплаты');
  }

  return response.json();
};

export interface ChangeTariffPayload {
  tariff_id: number;
  update_protocol_limit?: boolean;
  reset_balance_to_total?: boolean;
}

export const changeSubscriptionTariff = async (
  data: ChangeTariffPayload
): Promise<SubscriptionDetailsResponse> => {
  const response = await apiPost('/subscriptions/change-tariff', data);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка смены тарифа');
  }

  return response.json();
};

export const resetSubscriptionLimits = async (): Promise<SubscriptionDetailsResponse> => {
  const response = await apiPost('/subscriptions/reset-limits', {});

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка сброса лимитов');
  }

  return response.json();
};

export const getActiveSubscription = async (): Promise<SubscriptionDetailsResponse> => {
  const response = await apiGet('/subscriptions/active-subscription');

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка загрузки активной подписки');
  }

  return response.json();
};

export const getActiveTariffs = async (): Promise<AdminTariff[]> => {
  const response = await apiGet('/subscriptions/active-tariffs');

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка загрузки активных тарифов');
  }

  return response.json();
};

// -----------------------------
// Организации
// -----------------------------

export interface OrganizationsListResponse {
  organizations: Organization[];
  total: number;
  limit: number;
  offset: number;
  has_next: boolean;
}

export interface OrganizationParams {
  limit?: number;
  offset?: number;
  search?: string;
  sort_by?: 'name' | 'created_at' | 'updated_at';
  sort_order?: 'asc' | 'desc';
}

export interface CreateOrganizationData {
  name: string;
}

export interface UpdateOrganizationData {
  name: string;
}

// Получить список организаций
export const getOrganizations = async (params?: OrganizationParams): Promise<OrganizationsListResponse> => {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        searchParams.append(key, value.toString());
      }
    });
  }

  const queryString = searchParams.toString();
  const url = queryString ? `/organizations?${queryString}` : '/organizations';
  const response = await apiGet(url);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка загрузки организаций');
  }
  return response.json();
};

// Получить детали организации
export const getOrganizationDetails = async (organizationId: number): Promise<Organization> => {
  const response = await apiGet(`/organizations/${organizationId}`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка загрузки данных организации');
  }
  return response.json();
};

// Создать организацию
export const createOrganization = async (data: CreateOrganizationData): Promise<Organization> => {
  const response = await apiPost('/organizations', data);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка создания организации');
  }
  return response.json();
};

// Обновить организацию
export const updateOrganization = async (organizationId: number, data: UpdateOrganizationData): Promise<Organization> => {
  const response = await apiPut(`/organizations/${organizationId}`, data);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка обновления организации');
  }
  return response.json();
};

// Удалить организацию
export const deleteOrganization = async (organizationId: number): Promise<void> => {
  const response = await apiDelete(`/organizations/${organizationId}`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка удаления организации');
  }
};

// -----------------------------
// Специальности
// -----------------------------

export interface SpecialtiesListResponse {
  specialties: Specialty[];
  total: number;
  limit: number;
  offset: number;
  has_next: boolean;
}

export interface SpecialtiesForUserResponse {
  specialties: SpecialtyWithSelection[];
  total: number;
  limit: number;
  offset: number;
  has_next: boolean;
}

export interface SpecialtyParams {
  limit?: number;
  offset?: number;
  search?: string;
  organization_id?: number;
  sort_by?: 'name' | 'created_at' | 'updated_at';
  sort_order?: 'asc' | 'desc';
  /** Только для супер-админа; иначе API вернёт 403 */
  include_inactive?: boolean;
}

export interface CreateSpecialtyData {
  name: string;
  description?: string;
  structure?: string;
  organization_id?: number;
  profession?: string;
}

export interface UpdateSpecialtyData {
  name?: string;
  description?: string;
  structure?: string;
  organization_id?: number;
  profession?: string;
  is_active?: boolean;
}

// Получить список специальностей
export const getSpecialties = async (params?: SpecialtyParams): Promise<SpecialtiesListResponse> => {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        searchParams.append(key, value.toString());
      }
    });
  }

  const queryString = searchParams.toString();
  const url = queryString ? `/specialties?${queryString}` : '/specialties';
  const response = await apiGet(url);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка загрузки специальностей');
  }
  return response.json();
};

// Получить детали специальности
export const getSpecialtyDetails = async (specialtyId: number): Promise<Specialty> => {
  const response = await apiGet(`/specialties/${specialtyId}`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка загрузки данных специальности');
  }
  return response.json();
};

// Получить все доступные специальности
export const getAvailableSpecialties = async (params?: {
  include_inactive?: boolean;
}): Promise<Specialty[]> => {
  const searchParams = new URLSearchParams();
  if (params?.include_inactive === true) {
    searchParams.append('include_inactive', 'true');
  }
  const query = searchParams.toString();
  const url = query ? `/specialties/available/all?${query}` : '/specialties/available/all';
  const response = await apiGet(url);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка загрузки доступных специальностей');
  }
  const data = await response.json();
  // API может возвращать либо массив напрямую, либо объект со свойством specialties
  return Array.isArray(data) ? data : (data.specialties || []);
};

// Получить специальности по организации
export const getSpecialtiesByOrganization = async (organizationId: number): Promise<Specialty[]> => {
  const response = await apiGet(`/specialties/by-organization/${organizationId}`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка загрузки специальностей организации');
  }
  const data: SpecialtiesListResponse = await response.json();
  return data.specialties || [];
};

// Получить специальности для пользователя с флагами выбора
export const getSpecialtiesForUser = async (
  userId: number,
  params?: {
    limit?: number;
    offset?: number;
    search?: string;
    organization_id?: number;
    sort_by?: 'name' | 'created_at' | 'updated_at';
    sort_order?: 'asc' | 'desc';
    /** Только для супер-админа; иначе API вернёт 403 */
    include_inactive?: boolean;
  }
): Promise<SpecialtiesForUserResponse> => {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        searchParams.append(key, value.toString());
      }
    });
  }

  const queryString = searchParams.toString();
  const url = queryString
    ? `/specialties/for-user/${userId}?${queryString}`
    : `/specialties/for-user/${userId}`;

  const response = await apiGet(url);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка загрузки специальностей для пользователя');
  }
  return response.json();
};

// Создать специальность
export const createSpecialty = async (data: CreateSpecialtyData): Promise<Specialty> => {
  const response = await apiPost('/specialties', data);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка создания специальности');
  }
  return response.json();
};

// Обновить специальность
export const updateSpecialty = async (specialtyId: number, data: UpdateSpecialtyData): Promise<Specialty> => {
  const response = await apiPut(`/specialties/${specialtyId}`, data);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка обновления специальности');
  }
  return response.json();
};

// Удалить специальность
export const deleteSpecialty = async (specialtyId: number): Promise<void> => {
  const response = await apiDelete(`/specialties/${specialtyId}`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка удаления специальности');
  }
};

const formatApiErrorDetail = (detail: unknown): string | undefined => {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const parts = detail.map((d) => (typeof d === 'object' && d && 'msg' in d ? String((d as { msg: unknown }).msg) : String(d)));
    return parts.filter(Boolean).join('; ');
  }
  return undefined;
};

// Обновить специальности пользователя
export const updateUserSpecialties = async (userId: number, specialtyIds: number[]): Promise<AdminUser> => {
  const response = await apiPut(`/auth/admin/users/${userId}/specialties`, { specialty_ids: specialtyIds });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const msg =
      formatApiErrorDetail((errorData as { detail?: unknown }).detail) ||
      'Ошибка обновления специальностей пользователя';
    throw new Error(msg);
  }
  return response.json();
};

// -----------------------------
// Города МИС
// -----------------------------

export interface City {
  code: string;
  name: string;
}

export interface CitiesResponse {
  cities: City[];
}

// Получить список городов для МИС
export const getCities = async (): Promise<CitiesResponse> => {
  const response = await apiGet('/cities');
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка загрузки городов');
  }

  return response.json();
};

// -----------------------------
// Feedback по ответам ИИ
// -----------------------------

export type AIFeedbackStatus = 'unresolved' | 'resolved';
export type AIFeedbackSortBy = 'time' | 'status';
export type AIFeedbackOrder = 'asc' | 'desc';

export interface AIFeedbackUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
}

export interface AIFeedbackItem {
  id: number;
  user: string | AIFeedbackUser;
  message_id: number | null;
  chat_id: number | null;
  specialty_name: string | null;
  organization_name: string | null;
  ai_response_text: string;
  user_comment: string | null;
  status: AIFeedbackStatus;
  resolved_at: string | null;
  created_at: string;
}

export interface AIFeedbackListResponse {
  items: AIFeedbackItem[];
  total: number;
  skip: number;
  limit: number;
  has_next: boolean;
}

export interface CreateAIFeedbackPayload {
  message_id?: number;
  ai_response_text: string;
  user_comment?: string;
}

export const createAIFeedback = async (data: CreateAIFeedbackPayload): Promise<AIFeedbackItem> => {
  const response = await apiPost('/ai-feedback/', data);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка отправки отзыва');
  }

  return response.json();
};

export const getAIFeedbackAdminList = async (params: {
  skip?: number;
  limit?: number;
  sort_by?: AIFeedbackSortBy;
  order?: AIFeedbackOrder;
  organization_id?: number;
}): Promise<AIFeedbackListResponse> => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value.toString());
    }
  });

  const response = await apiGet(`/ai-feedback/admin?${searchParams}`);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка загрузки отзывов');
  }

  return response.json();
};

export const resolveAIFeedback = async (feedbackId: number): Promise<AIFeedbackItem> => {
  const response = await apiPatch(`/ai-feedback/admin/${feedbackId}/resolve`);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка изменения статуса');
  }

  return response.json();
};

export const unresolveAIFeedback = async (feedbackId: number): Promise<AIFeedbackItem> => {
  const response = await apiPatch(`/ai-feedback/admin/${feedbackId}/unresolve`);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Ошибка изменения статуса');
  }

  return response.json();
};